import { cloneElement, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';
import { useResizeObserver } from 'usehooks-ts';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import useIsTruncated from 'hooks/useIsTruncated';
import { WebEDIError } from 'services/webediErrors/types';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { classNames } from 'support/helpers/generic/generic';
import { ErrorIcon } from 'components/DataTable/components/Icons';
import { WebEDIRowData } from 'components/DataTable/Types';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';
import { useValue } from 'components/WebEDI/helpers';

import { CellProps } from '../../types';
import { getCellNestedLevel, useCellClassNames } from '../cellHelpers';
import { NestedLevelAddon } from '../components/NestedLevelAddon/NestedLevelAddon';
import { ViewWrapperCell } from './ViewWrapperCell';

export type Props = CellContext<WebEDIRowData, unknown> & Pick<CellProps, 'fieldId' | 'isHighlighted'>;

export const ViewTextCell = ({ row, cell, column, table, isHighlighted, fieldId }: Props) => {
  const { t } = useTranslation();
  const { service } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);
  const value = useValue(row.original, fieldId, column.columnDef.meta?.optionalConfig?.expression);
  const {
    columnDef: { meta: columnMeta },
  } = column;

  const formatValue = useMemo(
    () => columnMeta?.optionalConfig?.translateValue || ((value: any) => value),
    [columnMeta?.optionalConfig?.translateValue],
  );

  const cellClassNames = useCellClassNames({
    row,
    table,
    isFirstColumn: column.getIsFirstColumn(),
    isLastColumn: column.getIsLastColumn(),
  });
  const cellNestedLevel = columnMeta?.showNestLevel ? getCellNestedLevel(row) : 0;
  const [truncatedRef, valueIsTruncated] = useIsTruncated();

  const iconRef = useRef<HTMLDivElement>(null);
  const { width: iconWidth } = useResizeObserver({
    ref: iconRef,
    box: 'border-box',
  });

  return (
    <ViewWrapperCell className={cellClassNames} isHighlighted={isHighlighted} fieldId={fieldId} meta={columnMeta}>
      <Tooltip enabled={valueIsTruncated}>
        <TooltipTrigger>
          <span
            className="flex w-full items-center"
            style={{
              width: columnMeta?.optionalConfig?.width ? columnMeta.optionalConfig.width - (iconWidth || 0) : undefined,
            }}
          >
            <div
              className={classNames('flex w-full gap-x-0.5 overflow-hidden', {
                'justify-start': columnMeta?.optionalConfig?.textAlign === 'left',
              })}
            >
              {cellNestedLevel > 1 ? (
                <span className="pr-1.5">
                  <NestedLevelAddon level={cellNestedLevel} />
                </span>
              ) : null}
              {!value && error?.type === 'required' ? (
                t('common:table.errors.requiredField')
              ) : value ? (
                <span className="flex-initial truncate" ref={truncatedRef}>
                  {formatValue(value)}
                </span>
              ) : (
                <span className={classNames({ 'text-gray-400': !error })}>{EMPTY_FIELD}</span>
              )}
            </div>
            {columnMeta?.rightAddon && !error ? (
              <span className="text-gray-500" ref={iconRef}>
                {cloneElement(columnMeta.rightAddon, { cell })}
              </span>
            ) : null}
            {error ? <ErrorIcon ref={iconRef} /> : null}
          </span>
        </TooltipTrigger>
        <TooltipContent>{formatValue(value)}</TooltipContent>
      </Tooltip>
    </ViewWrapperCell>
  );
};
